<template>
  <div class="chart-container" :style="{ height: chartHeight + 'px' }">
    <canvas ref="canvasRef" />
  </div>
</template>

<script>
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Chart } from 'chart.js';
import { CHART_COLORS } from '../chartSpec.js';

/**
 * Bar chart using chart.js directly (vue-chartjs v5 is Vue 3 only; we use
 * chart.js with a canvas ref instead). Chart.js is registered globally in
 * main.js so no imports needed here beyond the Chart class.
 *
 * Lifecycle: create on mount, destroy on unmount, re-render on spec changes
 * by updating the existing Chart instance's data + options rather than
 * tearing it down — smoother when props change.
 */
export default defineComponent({
  name: 'BarChart',
  props: {
    spec: { type: Object, required: true },
  },
  setup(props) {
    const canvasRef = ref(null);
    let chart = null;

    // Give horizontal charts more height when there are many labels so labels
    // don't crush; vertical charts get a fixed comfortable height.
    const chartHeight = computed(() => {
      if (props.spec.orientation === 'horizontal') {
        return Math.max(200, props.spec.data.length * 28 + 80);
      }
      return 280;
    });

    function buildConfig() {
      return {
        type: 'bar',
        data: {
          labels: props.spec.data.map((d) => d.label),
          datasets: [
            {
              label: props.spec.title || '',
              data: props.spec.data.map((d) => d.value),
              // Single-series charts (e.g. spend over time) can pin one colour
              // via spec.color; categorical charts keep the cycling palette.
              backgroundColor: props.spec.color
                || props.spec.data.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
              borderRadius: 4,
              borderSkipped: false,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: props.spec.orientation === 'horizontal' ? 'y' : 'x',
          plugins: {
            title: { display: false },
            legend: { display: false },
            tooltip: {
              backgroundColor: 'rgba(17, 24, 39, 0.92)',
              padding: 8,
              cornerRadius: 6,
              titleFont: { size: 12 },
              bodyFont: { size: 12 },
            },
          },
          scales: {
            x: {
              title: {
                display: !!props.spec.x_label,
                text: props.spec.x_label || '',
                font: { size: 11 },
              },
              ticks: { font: { size: 11 } },
              grid: { display: false },
            },
            y: {
              title: {
                display: !!props.spec.y_label,
                text: props.spec.y_label || '',
                font: { size: 11 },
              },
              ticks: { font: { size: 11 }, precision: 0 },
              beginAtZero: true,
            },
          },
        },
      };
    }

    onMounted(() => {
      if (canvasRef.value) {
        chart = new Chart(canvasRef.value.getContext('2d'), buildConfig());
      }
    });

    watch(
      () => props.spec,
      () => {
        if (!chart) return;
        const cfg = buildConfig();
        chart.data = cfg.data;
        chart.options = cfg.options;
        chart.update();
      },
      { deep: true },
    );

    onBeforeUnmount(() => {
      if (chart) {
        chart.destroy();
        chart = null;
      }
    });

    return { canvasRef, chartHeight };
  },
});
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
}
</style>
